// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// The decided-plan card of Home › Closed (ui-mocks/EnergyFluo v1.html, the
// closed queue's recommendation tile): kind label + decision pill, title,
// "Day-ahead · JUL 24", and the two savings stats — sized like the alarm tile
// beside it, unlike the full PlanCard the Recommendations screen renders.

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { HitlApproval, HitlApprovalState } from '../../types';
import { KindChip } from './alarms/AlarmCard';
import { getNumberAttr, getStringAttr, parseExecutionStart } from '../../utils/planAttributes';
import { formatInZone } from '../../utils/siteTime';

const DECISION: Record<HitlApprovalState, { label: string; accent: string; className: string }> = {
  APPROVED: { label: 'Approved', accent: 'var(--st-ok)', className: 'hs-card-status-approved' },
  REJECTED: { label: 'Rejected', accent: 'var(--sev1)', className: 'hs-card-status-rejected' },
  PENDING: { label: 'Pending', accent: 'var(--interactive)', className: 'hs-card-status-pending' },
};

/** "−$12.50" / "160 kWh": the sign stays, placed before the unit. */
function signed(value: number, digits: number, unit = ''): string {
  const magnitude = Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: digits });
  return `${value < 0 ? '\u2212' : ''}${unit}${magnitude}`;
}

function Stat({ label, value, text }: { label: string; value: number; text: string }) {
  return (
    <div>
      <div className="hs-closed-plan-stat-label">{label}</div>
      <div
        className={`hs-closed-plan-stat-value${value < 0 ? ' hs-closed-plan-stat-value--negative' : ''}`}
      >
        {text}
      </div>
    </div>
  );
}

export function ClosedPlanCard({ approval }: { approval: HitlApproval }) {
  const navigate = useNavigate();
  const location = useLocation();
  const attributes = approval.attributes;
  const decision = DECISION[approval.state];
  const title = getStringAttr(attributes, 'title') ?? 'Optimization plan';
  const type = getStringAttr(attributes, 'optimizationType') ?? 'day-ahead';
  // executionStart is a UTC day boundary: format in UTC or it slides a day west of Greenwich.
  const executes = parseExecutionStart(getStringAttr(attributes, 'executionStart'));
  const executesDate = executes ? formatInZone(executes, 'dateShort', 'UTC', '').toUpperCase() : '';
  // Signed, like PlanCard's stats: energy vs. baseline is stored negative for
  // a reduction, so "saved" is its negation; a plan that used more energy or
  // cost money reads as a negative saving, in red, not as a green magnitude.
  const vsBaseline = getNumberAttr(attributes, 'energyVsBaselineKwh');
  const kwhSaved = vsBaseline === undefined ? undefined : -vsBaseline;
  const savings = getNumberAttr(attributes, 'totalCostSavings');
  const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);

  const openDetail = () =>
    navigate(`/optimizations/${approval.approvalId}`, {
      state: { from: `${location.pathname}${location.search}` },
    });

  return (
    <button
      type="button"
      className="hs-alarm-card hs-alarm-card--tile hs-closed-plan"
      style={{ borderLeftColor: decision.accent }}
      onClick={openDetail}
      data-testid="plan-card"
      data-plan-id={approval.approvalId}
    >
      <div className="hs-alarm-card-head">
        <KindChip label="Recommendation" />
        <span className={`hs-card-status ${decision.className}`} data-testid="plan-status">
          {decision.label}
        </span>
      </div>
      <div className="hs-closed-plan-title">{title}</div>
      <div className="hs-alarm-card-meta">
        {typeLabel}
        {executesDate && ` · ${executesDate}`}
      </div>
      {(kwhSaved !== undefined || savings !== undefined) && (
        <div className="hs-closed-plan-stats">
          {kwhSaved !== undefined && (
            <Stat label="Energy saved" value={kwhSaved} text={`${signed(kwhSaved, 0)} kWh`} />
          )}
          {savings !== undefined && (
            <Stat label="Bill savings" value={savings} text={signed(savings, 2, '$')} />
          )}
        </div>
      )}
    </button>
  );
}

export default ClosedPlanCard;
